/**
 * Storage Utility
 * 
 * Centralized abstraction for localStorage with type safety, validation,
 * expiration support, and error handling.
 */

import { logger } from './logger';

// Storage keys enum for type safety
export const STORAGE_KEYS = {
  // Progress and learning data
  PROGRESS: 'emmy-learning-progress',
  LEARNING_STREAK: 'learning-streak',
  LAST_LEARNING_DATE: 'last-learning-date',
  FEEDBACK: 'emmy-feedback',
  PARENT_MODE: 'emmy-parent-mode',
  ADAPTIVE_LEARNING: 'emmy-adaptive-learning-data',
  
  // User and profile
  GUEST_PROFILE: 'guestProfile',
  IS_GUEST: 'isGuest',
  USER_PROFILE: 'userProfile',
  USER_ID: 'emmy-user-id',
  
  // Configuration and flags
  SUPABASE_DISABLED: 'supabaseDisabled',
  DEVELOPER_MODE: 'developerMode',
  DATA_MIGRATION_COMPLETE: 'dataMigrationComplete',
  LEARNING_DATA_BACKUP: 'emmyLearningDataBackup',
  LEARNING_DATA_LEGACY: 'emmyLearningData',
  GAME_SETTINGS: 'emmy-game-settings',
  
  // OAuth
  OAUTH_FLOW_ID: 'oauthFlowId',
  OAUTH_LOGS: 'oauth-logs',
  
  // Offline and caching
  OFFLINE_QUEUE: 'emmy-offline-queue',
  CACHED_CONTENT: 'emmy-cached-content',
  
  // Streak system
  LONGEST_STREAK: 'emmy-longest-streak',
  TOTAL_LEARNING_DAYS: 'emmy-total-learning-days',
  
  // Theme and preferences
  THEME: 'emmy-theme',
  DAILY_CHALLENGE: 'emmy-daily-challenge',
  VOICE_SETTINGS: 'emmy-voice-settings',
  
  // Legacy keys (for migration)
  GUEST_PROFILE_LEGACY: 'guestProfile',
  LEARNING_DATA_LEGACY: 'emmyLearningData'
};

/**
 * Storage utility class
 */
class Storage {
  constructor() {
    this.prefix = 'emmy-';
    this.maxSize = 5 * 1024 * 1024; // 5MB warning threshold
  }

  /**
   * Get value from storage
   */
  get(key, defaultValue = null) {
    try {
      const item = localStorage.getItem(key);
      
      if (item === null) {
        return defaultValue;
      }

      // Check if item is already a plain string (not JSON)
      // This handles cases where dates or strings were stored directly
      if (item && !item.trim().startsWith('{') && !item.trim().startsWith('[') && !item.trim().startsWith('"')) {
        // Handle boolean strings - migrate them to proper JSON
        const trimmedItem = item.trim();
        if (trimmedItem === 'true' || trimmedItem === 'false') {
          const boolValue = trimmedItem === 'true';
          logger.debug('Migrating boolean string to JSON:', { key, value: boolValue });
          // Save as proper JSON
          this.set(key, boolValue);
          return boolValue;
        }
        
        // Try to parse as date string
        const dateValue = new Date(item);
        if (!isNaN(dateValue.getTime()) && item.includes(' ')) {
          // It's a date string like "Fri Oct 31 2025"
          logger.debug('Found non-JSON date string in storage, migrating:', { key, value: item });
          // Remove the invalid entry and return default
          this.remove(key);
          return defaultValue;
        }
        // Not a date, but also not JSON - log at debug level (not warning) and clean up silently
        logger.debug('Found non-JSON string in storage, cleaning up:', { key, value: item.substring(0, 50) });
        this.remove(key);
        return defaultValue;
      }

      try {
        const parsed = JSON.parse(item);
        
        // Check if item has expiry
        if (parsed && typeof parsed === 'object' && parsed.expiry !== undefined) {
          if (Date.now() > parsed.expiry) {
            // Item expired, remove it
            this.remove(key);
            return defaultValue;
          }
          return parsed.value;
        }

        return parsed;
      } catch (parseError) {
        // JSON parse failed - might be corrupted data
        logger.warn('Failed to parse storage item as JSON, removing:', { key, error: parseError.message });
        this.remove(key);
        return defaultValue;
      }
    } catch (error) {
      logger.error('Storage get error:', error, { key });
      // Try to clean up the problematic item
      try {
        this.remove(key);
      } catch (removeError) {
        // Ignore removal errors
      }
      return defaultValue;
    }
  }

  /**
   * Set value in storage
   */
  set(key, value) {
    try {
      const serialized = JSON.stringify(value);
      
      // Check size warning
      if (serialized.length > this.maxSize) {
        logger.warn('Large storage item detected:', { key, size: serialized.length });
      }

      localStorage.setItem(key, serialized);
      return true;
    } catch (error) {
      // Handle quota exceeded error
      if (error.name === 'QuotaExceededError' || error.code === 22) {
        logger.error('Storage quota exceeded. Attempting cleanup...', { key });
        this.cleanup();
        
        // Try once more after cleanup
        try {
          localStorage.setItem(key, JSON.stringify(value));
          return true;
        } catch (retryError) {
          logger.error('Storage set failed after cleanup:', retryError, { key });
          return false;
        }
      }
      
      logger.error('Storage set error:', error, { key });
      return false;
    }
  }

  /**
   * Set value with expiration
   */
  setWithExpiry(key, value, expiryMs) {
    const item = {
      value,
      expiry: Date.now() + expiryMs
    };
    return this.set(key, item);
  }

  /**
   * Remove item from storage
   */
  remove(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      logger.error('Storage remove error:', error, { key });
      return false;
    }
  }

  /**
   * Clear all storage (use with caution)
   */
  clear() {
    try {
      localStorage.clear();
      return true;
    } catch (error) {
      logger.error('Storage clear error:', error);
      return false;
    }
  }

  /**
   * Check if key exists
   */
  has(key) {
    return localStorage.getItem(key) !== null;
  }

  /**
   * Get all keys with a specific prefix
   */
  getKeys(prefix = '') {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keys.push(key);
      }
    }
    return keys;
  }

  /**
   * Get storage size estimate
   */
  getSize() {
    let total = 0;
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        total += localStorage[key].length + key.length;
      }
    }
    return total;
  }

  /**
   * Cleanup expired items and old data
   */
  cleanup() {
    const keysToRemove = [];
    
    try {
      // Find expired items
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;

        try {
          const item = localStorage.getItem(key);
          if (item) {
            const parsed = JSON.parse(item);
            if (parsed && typeof parsed === 'object' && parsed.expiry !== undefined) {
              if (Date.now() > parsed.expiry) {
                keysToRemove.push(key);
              }
            }
          }
        } catch (e) {
          // Skip invalid items
          continue;
        }
      }

      // Remove expired items
      keysToRemove.forEach(key => this.remove(key));

      if (keysToRemove.length > 0) {
        logger.info(`Cleaned up ${keysToRemove.length} expired storage items`);
      }

      return keysToRemove.length;
    } catch (error) {
      logger.error('Storage cleanup error:', error);
      return 0;
    }
  }

  /**
   * Get multiple values at once
   */
  getMultiple(keys) {
    const result = {};
    keys.forEach(key => {
      result[key] = this.get(key);
    });
    return result;
  }

  /**
   * Set multiple values at once
   */
  setMultiple(items) {
    const results = {};
    Object.entries(items).forEach(([key, value]) => {
      results[key] = this.set(key, value);
    });
    return results;
  }
}

// Create singleton instance immediately
// Safe to do now since logger no longer has circular dependencies
export const storage = new Storage();

// Convenience exports
export default storage;

